// src/pages/BlockForm.js
import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPencilAlt, faTrashAlt } from '@fortawesome/free-solid-svg-icons';

import { deleteAmenity } from '../services/AmenityServices';
import { deleteUnit } from '../services/UnitServices';
import { getBlockById, saveBlock } from '../services/BlockServices';
import { getStageById } from '../services/StageServices';
import { MediaContext } from '../context/MediaContext';
import MediaItems from '../components/MediaItems';

import './Forms.css';
import styles from './Table.module.css';
import { getProjectById } from '../services/ProjectServices';
import { getHousingTypologyById } from '../services/HousingTypologyServices';
import { getRotatorsByProyectId } from '../services/RotatorServices';

function BlockForm() {
  const { id, stageId } = useParams(); // Obtenemos el ID del Block y el ID del Stage
  const [amenities, setAmenities] = useState([]);
  const [block, setBlock] = useState({
    createdAt: "",
    code: '',
    name: '',
    description: '',
    buildPrice: '',
    balconyPrice: '',
    mezzaninePrice: '',
    orbitViews: '',
    tarracePrice: '',
    glbCode: '',    
    slug: '',
    mediaItems: []
  });
  const [error, setError] = useState(null);
  const [housingTypology, setHousingTypology] = useState(null);
  const {mediaItems, setMediaItems} = useContext(MediaContext);
  const [orbitViews, setOrbitViews] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [projectSlug, setProjectSlug] = useState('');
  const [stageSlug, setStageSlug] = useState('');
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updateCounter, setUpdateCounter] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteInfo, setDeleteInfo] = useState({ type: null, id: null });
  const [mediaSaveMessage, setMediaSaveMessage] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Obtener el slug de la etapa
        const stage = await getStageById(stageId);
        setStageSlug(stage.slug);
        setProjectId(stage.projectId);
        setPriceLists(stage.priceLists);
        const project = await getProjectById(stage.projectId);
        const htData = await getHousingTypologyById(stage.housingTypology);
        setHousingTypology(htData);
        setProjectSlug(project.slug);

        // Si estamos editando un bloque, obtener sus datos
        if (id) {
          const blockData = await getBlockById(id);
          setBlock(blockData);
          setUnits(blockData.units);
          setAmenities(blockData.amenities);
          setMediaItems(blockData.mediaItems);
          const orbitViewList = await getRotatorsByProyectId(stage.projectId);
          setOrbitViews(orbitViewList);
        } else {
          setMediaItems([]);
          setBlock((prevBlock) => ({ ...prevBlock, stageId: stageId, smlvPrice: htData.maxPrice }));
          const today = new Date().toISOString().split("T")[0];
          setBlock(prev => ({ ...prev, createdAt: today }));
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, stageId, setMediaItems, updateCounter]);

  const openDeleteModal = (type, id) => {
    setDeleteInfo({ type, id });
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteInfo({ type: null, id: null });
  };

  const confirmDelete = async () => {
    if (!deleteInfo.type || !deleteInfo.id) return;
    setLoading(true);
    try {
      if (deleteInfo.type === 'unit') {
        await handleDeleteUnit(deleteInfo.id);
      } else if (deleteInfo.type === 'amenity') {
        await handleDeleteAmenity(deleteInfo.id);
      }
    } finally {
      setLoading(false);
      closeDeleteModal();
    }
  };
  const handleCodeChange = (e) => {
    const newCode = e.target.value;
    const codeSlug = newCode.toLowerCase().replace(/ /g, '-');
    const newSlug = `${stageSlug}-${codeSlug}`;
    setBlock({ ...block, code: newCode, slug: newSlug });
  };

  const handleDeleteAmenity = async (id) => {
    setError(null);
    try {
      await deleteAmenity(id);
      setAmenities((prevAmenities) => prevAmenities.filter((amenity) => amenity.id !== id));
    } catch (error) {
      setError(error.message);
    }
  };

  const handleDeleteUnit = async (id) => {
    setError(null);
    setLoading(true);
    try {
      await deleteUnit(id);
      setUpdateCounter(updateCounter + 1);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!id) return;

    try {
      block.mediaItems = mediaItems;
      block.id = id;
      block.stageId = stageId;
      await saveBlock(block);
      setMediaSaveMessage("¡MediaItem guardado exitosamente!");
      setTimeout(() => setMediaSaveMessage(""), 3000);
    } catch (error) {
      setError(error.message);
    }
  }

  const handleSmlvPriceChange = (smlvPrice) => {
    const maxPrice = parseFloat(housingTypology.maxPrice);
    if (parseFloat(smlvPrice) > maxPrice) {
      smlvPrice = maxPrice;
    }
    setBlock({ ...block, smlvPrice: smlvPrice });
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      block.mediaItems = mediaItems;
      if (id)
        block.id = id;
      block.stageId = stageId;
      await saveBlock(block);
      navigate(`/stages/edit/${projectId}/${stageId}`);
    } catch (error) {
      setLoading(false);
      setError(error.message);
    }
  };

  if (loading) {
    return <div>Cargando...</div>;
  }

  return (
    <div className="page-container">
      <div className="form-container">
        <h2>{id ? 'Editar Bloque' : 'Nuevo Bloque'}</h2>
        {error && (
          <div>
            <div className='error-message'>
              Hubo un error: {error}
            </div>
            <br />
          </div>
        )}
        <form onSubmit={handleSubmit}>

          <div className="form-group">
            <label htmlFor="block-createdAt">Fecha de creación</label>
            <input
              type="date"
              id="block-createdAt"
              value={block.createdAt || ""}
              disabled
              readOnly  
            />
          </div>

          <div className="form-group">
            <label htmlFor="block-code">Código del Bloque</label>
            <input
              type="text"
              id="block-code"
              value={block.code}
              onChange={handleCodeChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="block-name">Nombre</label>
            <input
              type="text"
              id="block-name"
              value={block.name}
              onChange={(e) => setBlock({ ...block, name: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="block-description">Descripción</label>
            <textarea
              value={block.description}
              id="block-description"
              onChange={(e) => setBlock({ ...block, description: e.target.value })}
              required
            />
          </div>

          {housingTypology && housingTypology.priceCriteria === 'area' && (
            <div className="form-group">
              <label htmlFor="block-buildPrice">Lista de precios</label>
              <select
                id="block-buildPrice"
                value={block.priceList}
                onChange={(e) => setBlock({ ...block, priceList: e.target.value })}
                required
              >
                <option value="">Seleccione una lista de precios</option>
                {priceLists.map((priceList) => (
                  <option key={priceList.id} value={priceList.id}>{priceList.description}</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="block-orbitViews">Orbit Views</label>
            <select
              id="block-orbitViews"
              value={block.orbitViews}
              onChange={(e) => setBlock({ ...block, orbitViews: e.target.value })}>
              <option value="">Seleccione un órbit</option>
              {orbitViews.map((orbitViews) => (
                <option key={orbitViews.id} value={orbitViews.name}>
                  {orbitViews.name}
                </option>
              ))}
            </select>
          </div>

          {housingTypology && housingTypology.priceCriteria === 'smlv' && (
            <>
              <div className="form-group">
                <label htmlFor="block-smlvPrice">Precio unidad (SMLV)</label>
                <input
                  type="number"
                  id="block-smlvPrice"
                  value={block.smlvPrice}
                  onChange={(e) => handleSmlvPriceChange(e.target.value)}
                  required
                />
              </div>
            </>
          )}

          <div className="form-group">
            <label htmlFor="block-targetFrame">Frame en rotación 360°</label>
            <input
              type="number"
              id="block-targetFrame"
              value={block.targetFrame}
              onChange={(e) => setBlock({ ...block, targetFrame: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label htmlFor="block-glb">Código GLB</label>
            <input
              type="text"
              id="block-glb"
              value={block.glbCode}
              onChange={(e) => setBlock({ ...block, glbCode: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label htmlFor="block-slug">Slug (auto-generado)</label>
            <input
              type="text"
              id="block-slug"
              value={block.slug}
              readOnly
            />
          </div>

          <div className="form-group">
            <button type="submit" className="btn-submit">{id ? 'Guardar Cambios' : 'Agregar Bloque'}</button>
          </div>
        </form>
        <div className="form-footer">
          <Link to={`/stages/edit/${projectId}/${stageId}`} className="back-link">Regresar a Etapa</Link>
        </div>
      </div>

      {id && (
        <div className="stages-container">
          <div className="sections-container">
            <MediaItems projectSlug={projectSlug} objectType='block' objectSlug={block.slug} />
            <button className="btn-submit" onClick={() => handleSave()}>Grabar</button>
            {mediaSaveMessage && <div className="media-save-message">{mediaSaveMessage}</div>}
          </div>

          <div className="sections-container">
            <h2>Unidades</h2>
            <button className="btn-add" onClick={() => navigate(`/units/new/${id}`)}>
              <FontAwesomeIcon icon={faPlus} />
            </button>

            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: '60%' }}>Nombre de la Unidad</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {units.map((unit) => (
                  <tr key={unit.id}>
                    <td>{unit.name}</td>
                    <td>
                      <div className="actions-container">
                        <button className="btn-edit" onClick={() => navigate(`/units/edit/${id}/${unit.id}`)}>
                          <FontAwesomeIcon icon={faPencilAlt} />
                        </button>
                        <button className="btn-delete" onClick={() => openDeleteModal('unit', unit.id)}>
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>


          <div className="sections-container">
            <h2>Amenidades</h2>
            <button className="btn-add" onClick={() => navigate(`/amenities/new/block/${id}`)}>
              <FontAwesomeIcon icon={faPlus} />
            </button>

            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: '60%' }}>Nombre de la Amenidad</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {amenities.map((amenity) => (
                  <tr key={amenity.id}>
                    <td>{amenity.name}</td>
                    <td>
                      <div className="actions-container">
                        <button className="btn-edit" onClick={() => navigate(`/amenities/edit/block/${id}/${amenity.id}`)}>
                          <FontAwesomeIcon icon={faPencilAlt} />
                        </button>
                        <button className="btn-delete" onClick={() => openDeleteModal('amenity', amenity.id)}>
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {showDeleteModal && (
            <div className="media-modal-overlay" onClick={closeDeleteModal}>
              <div className="media-modal" onClick={e => e.stopPropagation()}>
                <h3 className="modal-content-h3">¿Seguro que deseas eliminar este archivo?</h3>
                <div style={{ display: "flex", gap: "10px", marginTop: "20px", justifyContent: "center" }}>
                  <button className="btn-submit" onClick={confirmDelete}>Aceptar</button>
                  <button className="btn-cancel" onClick={closeDeleteModal}>Cancelar</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default BlockForm;
